import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import PropTypes from "prop-types";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import ListItemText from "@mui/material/ListItemText";
import { useTheme } from "@mui/material/styles";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import GROUPS_CONSTANTS, { GROUP_ID_TYPES } from "./Customers.constants";
import {
  getGroupIdValidationError,
  GROUP_ID_MAX_LENGTH,
} from "./groupIdValidation.util";
import { useHachwimsFacilityOptions } from "./Customers.hook";
import { useSelectedCustomerAccess } from "./Components/SelectedCustomerAccessContext";
import {
  FACILITIES_LOADING_ID,
  FACILITIES_LOADING_OPTION_CLASS,
  FACILITIES_LOADER_SIZE,
  StyledFacilitiesLoader,
  StyledFacilitiesInputLoader,
  FacilitiesLoadingOption,
  FacilityOptionRow,
  FacilityOptionIconBox,
} from "./Components/DataConnectionsTabContent.style";
import { GenericForm } from "common/Components/GenericForm";
import { FormTextField, CustomizedSwitch } from "styles/formStyleComponents";
import {
  ConnectionHeader,
  ConnectionSection,
  ConnectionTitle,
  ConnectionsStack,
  facilitiesAutocompleteSx,
  facilitiesListboxStyle,
  facilitiesTagsWrapSx,
  getFacilitiesSlotProps,
  ConnectionStatus,
} from "./CustomerDetailsDrawer.styles";

const DC = GROUPS_CONSTANTS.groupAccessDrawer.DATA_CONNECTIONS;

const snapshot = (mqtt, hach, mqttOn = true, hachOn = true) => ({
  mqttSpb: String(mqtt ?? "").trim(),
  hachwims: Array.isArray(hach) ? [...hach] : [],
  mqttEnabled: Boolean(mqttOn),
  hachwimsEnabled: Boolean(hachOn),
});

const facilityOrderKey = (list) =>
  (Array.isArray(list) ? list : []).map((f) => f?.id).filter(Boolean).join("|");

const handleWhiteSpaces = (event) => {
  if (event.key === " " || event.keyCode === 32) {
    event.preventDefault();
  }
};

const mqttFormFields = (disabled) => [
  {
    name: "mqttSpb",
    label: DC.MQTT_SPB_FIELD_LABEL,
    type: "text",
    placeholder: DC.MQTT_SPB_PLACEHOLDER,
    disabled,
    validation: { maxLength: GROUP_ID_MAX_LENGTH },
  },
];

const CustomerDataConnectionsTab = forwardRef(
  ({ onApply, applying = false, groups, onApplyStateChange }, ref) => {
    const theme = useTheme();
    const selectedGroupForAccess = useSelectedCustomerAccess();
    const selectedMqttSource = selectedGroupForAccess?.dataConnections?.find(
      (o) => o?.source === "mqtt-spb"
    )?.groupId;
    const [mqttSpb, setMqttSpb] = useState(selectedMqttSource ?? "");
    const [groupIdError, setGroupIdError] = useState(null);
    const [facilitiesError, setFacilitiesError] = useState(null);
    const [selectedHachwims, setSelectedHachwims] = useState([]);
    const [baseline, setBaseline] = useState(() => snapshot("", [], true, true));
    const availableDataConnections = {
      mqttSpb: selectedGroupForAccess?.dataConnections?.some(
        (o) => o?.source === "mqtt-spb"
      ),
      hachwims: selectedGroupForAccess?.dataConnections?.some(
        (o) => o?.source === "hachwims"
      ),
    };
    const [mqttEnabled, setMqttEnabled] = useState(() =>
      Boolean(availableDataConnections?.mqttSpb)
    );
    const [hachwimsEnabled, setHachwimsEnabled] = useState(() =>
      Boolean(availableDataConnections?.hachwims)
    );

    useEffect(() => {
      const list = selectedGroupForAccess?.dataConnections ?? [];
      const hasMqtt = list.some((o) => o?.source === "mqtt-spb");
      const hasHach = list.some((o) => o?.source === "hachwims");
      const { groupId: gid = "" } =
        list.find((r) => r.source === "mqtt-spb") ?? {};
      const hach = list.find((o) => o?.source === "hachwims")?.facilities ?? [];
      const mqtt = String(gid).slice(0, GROUP_ID_MAX_LENGTH);
      const hachFacilities = hach.flatMap((f) => {
        if (f == null) return [];
        return [{ id: f.id, name: f.name }];
      });
      setMqttSpb(mqtt);
      setGroupIdError(null);
      setSelectedHachwims(hachFacilities);
      setBaseline(snapshot(mqtt, hachFacilities, hasMqtt, hasHach));
      setMqttEnabled(hasMqtt);
      setHachwimsEnabled(hasHach);
    }, [selectedGroupForAccess]);

    useEffect(() => {
      if (mqttEnabled && !mqttSpb?.trim()) {
        setGroupIdError(
          GROUPS_CONSTANTS.createGroupErrorMessages.idRequired.replace(
            "{idType}",
            GROUP_ID_TYPES.groupId
          )
        );
      }
    }, [mqttEnabled, mqttSpb]);

    useEffect(() => {
      if (hachwimsEnabled && selectedHachwims?.length === 0) {
        setFacilitiesError(
          GROUPS_CONSTANTS.createGroupErrorMessages.facilitiesRequired
        );
      } else {
        setFacilitiesError(null);
      }
    }, [hachwimsEnabled, selectedHachwims]);

    const {
      hachwimsDropdownOptions,
      loadingHachwimsOptions,
      onFacilityInputChange,
      onFacilityListboxScroll,
      onFacilitiesDropdownOpen,
      onFacilitiesDropdownClose,
    } = useHachwimsFacilityOptions(
      selectedHachwims,
      selectedGroupForAccess?.groupId
    );

    const hachwimsOptionsSelectedFirst = useMemo(() => {
      const opts = hachwimsDropdownOptions ?? [];
      const selected = Array.isArray(selectedHachwims) ? selectedHachwims : [];
      const selectedFilenames = new Set(
        selected.map((s) => s?.id).filter(Boolean)
      );
      const top = [];
      const seen = new Set();
      for (const sel of selected) {
        const fn = sel?.id;
        if (!fn || seen.has(fn)) continue;
        const match = opts.find((o) => o.id === fn);
        if (match) {
          top.push(match);
          seen.add(fn);
        }
      }
      const rest = opts.filter((o) => !selectedFilenames.has(o.id));
      return [...top, ...rest];
    }, [hachwimsDropdownOptions, selectedHachwims]);

    const hachwimsFacilityOptions = useMemo(() => {
      if (!loadingHachwimsOptions) return hachwimsOptionsSelectedFirst;
      return [
        ...hachwimsOptionsSelectedFirst,
        { id: FACILITIES_LOADING_ID, name: "" },
      ];
    }, [hachwimsOptionsSelectedFirst, loadingHachwimsOptions]);

    const hachwimsAutocompleteSlotProps = useMemo(
      () => getFacilitiesSlotProps(theme.zIndex.modal),
      [theme.zIndex.modal]
    );

    const hachwimsListboxProps = useMemo(
      () => ({
        onScroll: onFacilityListboxScroll,
        style: facilitiesListboxStyle,
      }),
      [onFacilityListboxScroll]
    );

    const trimmed = mqttSpb.trim();
    const togglesChanged =
      mqttEnabled !== baseline.mqttEnabled ||
      hachwimsEnabled !== baseline.hachwimsEnabled;
    const mqttSectionChanged = mqttEnabled && trimmed !== baseline.mqttSpb;
    const hachwimsSectionChanged =
      hachwimsEnabled &&
      facilityOrderKey(selectedHachwims) !== facilityOrderKey(baseline.hachwims);
    const hasChanges =
      mqttSectionChanged || hachwimsSectionChanged || togglesChanged;
    const applyDisabled =
      applying ||
      !hasChanges ||
      (mqttEnabled && Boolean(groupIdError)) ||
      (hachwimsEnabled && Boolean(facilitiesError));

    useEffect(() => {
      onApplyStateChange?.({ disabled: applyDisabled });
    }, [applyDisabled, onApplyStateChange]);

    const runGroupIdValidation = (val) => {
      const errorMessage = getGroupIdValidationError(
        val,
        groups,
        GROUP_ID_TYPES.groupId,
        baseline.mqttSpb
      );
      setGroupIdError(errorMessage);
      return errorMessage;
    };

    const handleApply = async () => {
      if (!hasChanges || typeof onApply !== "function") return;
      if (mqttEnabled && trimmed && runGroupIdValidation(mqttSpb)) return;

      const dataConnections = [];
      if (mqttEnabled) {
        dataConnections.push({
          source: "mqtt-spb",
          groupId: trimmed,
        });
      }
      if (hachwimsEnabled) {
        dataConnections.push({
          source: "hachwims",
          facilities: selectedHachwims
            ?.map((facility) => facility?.id)
            .filter(Boolean),
        });
      }
      const nextBaseline = snapshot(
        trimmed,
        selectedHachwims,
        mqttEnabled,
        hachwimsEnabled
      );
      if (dataConnections.length === 0) {
        setBaseline(nextBaseline);
        return;
      }

      try {
        await onApply({ dataConnections });
        setBaseline(nextBaseline);
      } catch {
        // Keep current form state; parent toast handles the error.
      }
    };

    useImperativeHandle(ref, () => ({
      apply: handleApply,
    }));

    const handleOnChangeMqtt = (next) => {
      if (next.length >= GROUP_ID_MAX_LENGTH) {
        runGroupIdValidation(next);
        return;
      }
      setMqttSpb(next);
      if (!next.trim()) {
        setGroupIdError(null);
      } else if (mqttEnabled) {
        runGroupIdValidation(next);
      }
    };

    return (
      <ConnectionsStack>
        <ConnectionSection>
          <ConnectionHeader>
            <CustomizedSwitch
              checked={mqttEnabled}
              disabled={mqttEnabled && selectedMqttSource}
              onChange={(_, checked) => {
                setMqttEnabled(checked);
                if (!checked) {
                  setGroupIdError(null);
                }
              }}
              inputProps={{ "aria-label": DC.MQTT_SPB_TOGGLE_ARIA }}
            />
            <ConnectionTitle>{DC.MQTT_SPB_TITLE}</ConnectionTitle>
            <ConnectionStatus>{mqttEnabled ? "On" : "Off"}</ConnectionStatus>
          </ConnectionHeader>
          <Box onKeyDown={handleWhiteSpaces}>
            <GenericForm
              fields={mqttFormFields(Boolean(mqttEnabled && selectedMqttSource))}
              values={{ mqttSpb }}
              errors={{ mqttSpb: groupIdError || "" }}
              onChange={(_fieldName, nextValue) => {
                handleOnChangeMqtt(nextValue);
              }}
              showBorder={false}
            />
          </Box>
        </ConnectionSection>

        <ConnectionSection>
          <ConnectionHeader>
            <CustomizedSwitch
              checked={hachwimsEnabled}
              onChange={(_, checked) => setHachwimsEnabled(checked)}
              inputProps={{ "aria-label": DC.HACHWIMS_TOGGLE_ARIA }}
            />
            <ConnectionTitle>{DC.HACHWIMS_TITLE}</ConnectionTitle>
            <ConnectionStatus>{hachwimsEnabled ? "On" : "Off"}</ConnectionStatus>
          </ConnectionHeader>
          <div>
            <Autocomplete
              multiple
              fullWidth
              disabled={!hachwimsEnabled}
              loading={loadingHachwimsOptions}
              options={hachwimsFacilityOptions}
              value={Array.isArray(selectedHachwims) ? selectedHachwims : []}
              onChange={(_, value) =>
                setSelectedHachwims(
                  (Array.isArray(value) ? value : []).filter(
                    (v) => v?.id !== FACILITIES_LOADING_ID
                  )
                )
              }
              getOptionDisabled={(option) => option?.id === FACILITIES_LOADING_ID}
              onOpen={onFacilitiesDropdownOpen}
              onClose={onFacilitiesDropdownClose}
              onInputChange={onFacilityInputChange}
              filterOptions={(options) => options}
              getOptionLabel={(o) => o?.name ?? ""}
              isOptionEqualToValue={(a, b) => a?.id === b?.id}
              disableCloseOnSelect
              slotProps={hachwimsAutocompleteSlotProps}
              ListboxProps={hachwimsListboxProps}
              sx={facilitiesAutocompleteSx}
              renderOption={(props, option, { selected }) => {
                const { key, ...rest } = props;
                if (option?.id === FACILITIES_LOADING_ID) {
                  const { style: _style, className, ...loadingOptionProps } = rest;
                  return (
                    <FacilitiesLoadingOption
                      key={key}
                      {...loadingOptionProps}
                      className={
                        className
                          ? `${className} ${FACILITIES_LOADING_OPTION_CLASS}`
                          : FACILITIES_LOADING_OPTION_CLASS
                      }
                    >
                      <StyledFacilitiesLoader
                        aria-label="Loading facilities"
                        size={FACILITIES_LOADER_SIZE}
                      />
                    </FacilitiesLoadingOption>
                  );
                }
                const SelectionIcon = selected
                  ? CheckBoxIcon
                  : CheckBoxOutlineBlankIcon;
                return (
                  <FacilityOptionRow key={key} component="li" {...rest}>
                    <FacilityOptionIconBox>
                      <SelectionIcon fontSize="small" />
                    </FacilityOptionIconBox>
                    <ListItemText
                      primary={option?.name ?? ""}
                      primaryTypographyProps={{ variant: "body2", noWrap: true }}
                    />
                  </FacilityOptionRow>
                );
              }}
              renderTags={(tagValue, getTagProps) =>
                tagValue.length === 0 ? null : (
                  <Box sx={facilitiesTagsWrapSx}>
                    {tagValue?.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        variant="outlined"
                        size="small"
                        label={option?.name ?? ""}
                      />
                    ))}
                  </Box>
                )
              }
              renderInput={(params) => (
                <FormTextField
                  {...params}
                  id="customer-hachwims-facilities"
                  label={DC.HACHWIMS_FIELD_LABEL}
                  placeholder={DC.HACHWIMS_PLACEHOLDER}
                  size="small"
                  error={Boolean(facilitiesError)}
                  helperText={facilitiesError || ""}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingHachwimsOptions ? (
                          <StyledFacilitiesInputLoader
                            aria-label="Loading facilities"
                            size={FACILITIES_LOADER_SIZE}
                          />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  inputProps={{
                    ...params.inputProps,
                    "aria-label": DC.HACHWIMS_ARIA_LABEL,
                  }}
                />
              )}
            />
          </div>
        </ConnectionSection>
      </ConnectionsStack>
    );
  }
);

CustomerDataConnectionsTab.displayName = "CustomerDataConnectionsTab";

CustomerDataConnectionsTab.propTypes = {
  onApply: PropTypes.func,
  applying: PropTypes.bool,
  groups: PropTypes.array,
  onApplyStateChange: PropTypes.func,
};

export default CustomerDataConnectionsTab;
